/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState } from 'react';
import { jsx, css } from '@emotion/react';
import { Spinner, Text, ChoiceGroup } from '@fluentui/react';
import useAxios from 'axios-hooks';
import IntegratedRewardCobrandsTable from '../Components/IntegratedRewardCobrandsTable';
import IntegratedRewardClientsTable from '../Components/IntegratedRewardClientsTable';
import DeleteModal from "../../../../Util/DeleteModal";
import { useBoolean } from '@fluentui/react-hooks';
import ErrorComponent from '../../../../Util/ErrorComponent';
import SuccessComponent from '../../../../Util/SuccessComponent';
import PostModal from '../../../../Util/PostModal';

const levelOptions = [
  { key: 0, text: 'client' },
  { key: 1, text: 'cobrand' },
];

const clientLevelDescription = "Select a client to disable/enable the Integrated Rewards dashboard feature for. Enabling will allow client's cobrands to be enabled/disabled. The cobrands are disabled by default when enabling at the client level.";
const cobrandLevelDescription = 'Select a cobrand to disable/enable the Integrated Rewards dashboard feature for. Enabling will allow users under that cobrand to see the Integrated Rewards dashboard. If there is not a custom cobrand dashboard, the client dashboard (if enabled) or default dashboard will be shown.';

const DefaultPage = () => {
    const [selectedItem, setSelectedItem] = useState(undefined);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [level, setLevel] = useState('');
    const [showDisableModal, { toggle: toggleDisableModal }] = useBoolean(false);
    const [showEnableModal, { toggle: toggleEnableModal }] = useBoolean(false);

    const [{ data: cobrands, cobrandsLoading }, refetchCobrands] = useAxios("integrated-rewards/dashboard/cobrand-settings");
    const [{ data: clients, clientsLoading }, refetchClients] = useAxios("integrated-rewards/dashboard/client-settings");
    
    const loading = cobrandsLoading || clientsLoading;

    if (loading) return <Spinner className="absolute-center" label={`Loading Integrated Reward ${level}s...`} />;

    return (
        <>
            <SuccessComponent
                successMessage="Integrated Reward Dashboard Setting Disabled."
                showSuccess={showSuccess}
                setShowSuccess={setShowSuccess}
            />
            <ErrorComponent
                errorMessage={errorMsg}
                showError={showError}
                setShowError={setShowError}
            />
            <ChoiceGroup
                label="Select the access level to disable/enable."
                options={levelOptions}
                onChange={(e, x) => setLevel(x.text)}
            />
            <div css={css`max-width: 800px;`}>
                <Text variant="small">
                    {level === '' ? 
                        "Select 'client' or 'cobrand' to disable/enable the Integrated Rewards dashboard feature."
                        : 
                        level === 'client' ? clientLevelDescription : cobrandLevelDescription}
                </Text>
            </div>
            <hr />
            {level === 'client' &&
                <IntegratedRewardClientsTable
                    clients={clients && clients.length > 0 ? clients.sort((a, b) => b.clientId - a.clientId) : []}
                    setSelectedItem={setSelectedItem}
                    selectedItem={selectedItem}
                    enableOnClick={() => toggleEnableModal()}
                    disableOnClick={() => toggleDisableModal()}
                    refetch={refetchClients}
                    loading={loading}
                />
            }
            {level === 'cobrand' && 
                <IntegratedRewardCobrandsTable
                    cobrands={cobrands && cobrands.length > 0 ? cobrands.sort((a, b) => b.cobrandId - a.cobrandId) : []}
                    setSelectedItem={setSelectedItem}
                    selectedItem={selectedItem}
                    enableOnClick={() => toggleEnableModal()}
                    disableOnClick={() => toggleDisableModal()}
                    refetch={refetchCobrands}
                    loading={loading}
                />
            }
             <PostModal
                showModal={showDisableModal}
                toggleShowModal={toggleDisableModal}
                onPost={level === 'client' ? () => refetchClients() : () => refetchCobrands()}
                setErrorMsg={setErrorMsg}
                setShowError={setShowError}
                setShowSuccess={setShowSuccess}
                postUrl={`integrated-rewards/${level}/disable/${selectedItem}/`}
                subText='Are you sure you want to disable the Integrated Reward dashboard feature?'
                postButtonText = "Disable"
            />
            <DeleteModal
                showModal={showEnableModal}
                toggleShowModal={toggleEnableModal}
                onDelete={level === 'client' ? () => refetchClients() : () => refetchCobrands()}
                setErrorMsg={setErrorMsg}
                setShowError={setShowError}
                setShowSuccess={setShowSuccess}
                deleteUrl={`integrated-rewards/${level}/enable/${selectedItem}/`}
                subText='Are you sure you want to enable the Integrated Reward dashboard feature?'
                deleteButtonText='Enable'
            />
        </>
    );
};

export default DefaultPage;
